// oxlint-disable max-depth
import { UNNAMED } from '@game/constants';
import { CombatStatus, PopupNotificationType } from '@game/enums';
import type { ChatCommand, PopulatedItem, WorkoutRecord } from '@game/game-data';
import type {
  ClientAddMissionPin,
  ClientArea,
  ClientChatMessage,
  ClientChatMessageHistory,
  ClientCombat,
  ClientCombatantStatic,
  ClientCombatReward,
  ClientCombatRound,
  ClientCombatTurn,
  ClientCombatTurnUpdate,
  ClientCorporation,
  ClientCorporationEventLog,
  ClientCorporationMissionRecord,
  ClientEquipmentItem,
  ClientInventoryItem,
  ClientLocation,
  ClientMail,
  ClientMarketBase,
  ClientMarketResults,
  ClientNotificationRecord,
  ClientParty,
  ClientPartyUserStatus,
  ClientUpdateCorporationPrivateDescription,
  ClientUpdateCorporationPublicDescription,
  ClientUserCorporationJoinRequest,
  CompleteCorporationMissionRecord,
  CorporationMissionBoardItem,
  ServerToClientEvents,
  UpdateChatAvatar,
} from '@game/shared-types';
import { errorNotification, infoNotification, successNotification, warningNotification } from '@game/ui';
import { playAreaMusic } from '@lib/musicManager';
import { playHitSfx, playKillingBlowSfx, playSkillSfx } from '@lib/soundManager';
import { playActionSound } from '@lib/sounds';
import type { Socket } from 'socket.io-client';
import { navigate } from 'wouter/use-browser-location';

import { localChatCommands } from '@components/Chat/helpers/handleLocalCommand';
import { COMBAT, LOGOUT } from '@constants/paths';
import { generateLocationsMenu } from '@util/generateLocationsMenu';
import { getPublicAvatarURL } from '@util/getPublicAvatarURL';
import { getSortedInventory } from '@util/getSortedInventory';
import { isObject } from '@util/isObject';
import { Logger } from '@util/logger';
import { authStore } from '@store/authStore';
import { defaultCombatReward } from '@store/combatState';
import { CombatScreenState, combatStore } from '@store/combatStore';
import { corporationStore } from '@store/corporationStore';
import { store as mailStore } from '@store/mailStore';
import { marketStore } from '@store/marketStore';
import { musicStore } from '@store/musicStore';
import { persistentStore } from '@store/persistentStore';
import { store } from '@store/store';
import { defaultLocation } from '@store/userState';

import type { CombatRoundCombatantsStats, CombatTurnData } from '../../types/combat.types';
import type { PopupNotification } from '../../types/ui.types';
import { MappedEquipment } from '../../views/Equipment/MappedEquipment';

function updateSortedInventory(): void {
  const inventoryState = persistentStore.getState().inventoryState;
  store.setState({ sortedInventory: getSortedInventory(store.getState().inventory, inventoryState) });
}

function updateCorporationSortedInventory(): void {
  const corporationInventoryState = persistentStore.getState().corporationInventoryState;
  corporationStore.setState({ sortedInventory: getSortedInventory(corporationStore.getState().inventory, corporationInventoryState) });
}

function updateMappedEquipment(): void {
  const equipment = store.getState().equipment;
  const mappedEquipment = MappedEquipment({ equipment });
  store.setState({ mappedEquipment });
}

function addCombatTurn(turnData: CombatTurnData): void {
  if (turnData.turn.nextSequenceKey) {
    combatStore.setState({ nextSequenceKey: turnData.turn.nextSequenceKey });
  }

  combatStore.getState().filterSequenceKeyFromCombatTurnQueue(turnData.turn.sequenceKey);
  combatStore.getState().addCombatLogHistory(turnData);
}

function handleData(data: unknown): void {
  if (isObject(data)) {
    for (const [key, value] of Object.entries(data)) {
      switch (key) {
        // TITLE: UI
        case 'serverTime': {
          const serverTime = value as number;
          const serverTimeOffset = Date.now() - serverTime;
          Logger.log('serverTimeOffset', serverTimeOffset);
          store.setState({ serverTime, serverTimeOffset });
          break;
        }
        // TITLE: NOTIFICATION
        case 'popupNotification': {
          const popupNotification = value as PopupNotification;
          switch (popupNotification.type) {
            case PopupNotificationType.Success: {
              successNotification({ message: popupNotification.message, toPath: popupNotification.toPath });
              break;
            }
            case PopupNotificationType.Warning: {
              warningNotification({ message: popupNotification.message, toPath: popupNotification.toPath });
              break;
            }
            case PopupNotificationType.Error: {
              errorNotification({
                message: popupNotification.message,
                toPath: popupNotification.toPath,
              });
              break;
            }
            case PopupNotificationType.Info: {
              infoNotification({ message: popupNotification.message, toPath: popupNotification.toPath });
              break;
            }
            default: {
              break;
            }
          }
          break;
        }
        case 'popupNotifications': {
          const popupNotifications = value as PopupNotification[];
          for (const popupNotification of popupNotifications) {
            handleData({ popupNotification });
          }
          break;
        }
        // TITLE: LOCATION UPDATE
        case 'updateLocation': {
          const currentLocation = store.getState().location;
          const newLocation = value as ClientLocation;
          if (currentLocation.name === newLocation.name) {
            store.setState({ location: newLocation });
          }
          break;
        }
        // TITLE: CHAT
        case 'chatChannelHistory': {
          const chatChannelHistory = value as ClientChatMessageHistory;

          for (const chatMessage of chatChannelHistory.messages) {
            handleData({ prependChatMessage: chatMessage });
          }

          // store.getState().addChatChannel(chatChannelHistory.channel);
          store.getState().reduceChatChannelFirstItemIndex(chatChannelHistory.channel.name, chatChannelHistory.messages.length);
          store.getState().setChatChannelHasOlderMessages(chatChannelHistory.channel.name, chatChannelHistory.hasOlderMessages);

          store.setState({ loadingMoreMessages: false });
          break;
        }
        case 'updateChatAvatar': {
          const update = value as UpdateChatAvatar;
          const avatarUrl = update.avatar ? getPublicAvatarURL(update.avatar) : '';
          store.getState().setChatAvatar(update.name, avatarUrl);
          break;
        }
        case 'allChatMessageHistory': {
          const chatMessageHistory = value as Record<string, ClientChatMessageHistory>;

          for (const [channel, history] of Object.entries(chatMessageHistory)) {
            history.messages.toReversed().forEach((chatMessage: ClientChatMessage) => {
              store.getState().addChatMessage(chatMessage);
            });

            // store.getState().addChatChannel(history.channel);
            store.getState().reduceChatChannelFirstItemIndex(channel, history.messages.length);
            store.getState().setChatChannelHasOlderMessages(channel, history.hasOlderMessages);
          }
          store.setState({ unreadChatMessageCount: 0 });
          break;
        }
        case 'addChatChannel': {
          const chatChannel = value as { name: string; alias: string };
          store.getState().addChatChannel(chatChannel);
          break;
        }
        case 'prependChatMessage': {
          const chatMessage = value as ClientChatMessage;
          const avatarUrl = chatMessage.user.avatar ? getPublicAvatarURL(chatMessage.user.avatar) : '';
          const storedAvatar = store.getState().chatAvatars[chatMessage.user.name];

          // NOTE: Only update the avatar if it wasn't already in the cache
          if (!storedAvatar) {
            // Add the avatar to the chatAvatars map, which is a cache of all the avatars
            store.getState().setChatAvatar(chatMessage.user.name, avatarUrl);
          }

          store.getState().prependChatMessage({
            ...chatMessage,
            user: {
              avatar: '', // This is not used.
              name: chatMessage.user.name,
              roles: chatMessage.user.roles,
              tags: chatMessage.user.tags,
            },
            channelName: chatMessage.channelName,
          });
          break;
        }
        case 'addChatMessage': {
          const chatMessage = value as ClientChatMessage;

          store.getState().addChatMessage(chatMessage);

          if (chatMessage.user.name !== store.getState().name) {
            playActionSound('RECEIVE_MESSAGE');
          }

          break;
        }

        case 'removeChatMessage': {
          const { channel, messageId } = value as { channel: string; messageId: string };

          store.getState().removeChatMessage(channel, messageId);

          break;
        }

        case 'adminItemSchematicNames': {
          store.setState({ adminItemSchematicNames: value as string[] });
          break;
        }

        case 'chatCommands': {
          const chatCommands = value as ChatCommand[];
          store.setState({ chatCommands: [...chatCommands, ...localChatCommands] });

          break;
        }
        // TITLE: RESOURCES
        case 'resourceTicks': {
          const resourceTicks = value as [number, number];
          const now = Date.now();
          const energyNextTick = new Date(now).getTime() + resourceTicks[0];
          const spiritNextTick = new Date(now).getTime() + resourceTicks[1];
          store.getState().updateResourceTicks(energyNextTick, spiritNextTick);
          break;
        }
        case 'energyNextTick': {
          const energyNextTickIncrement = value as number;
          const energyNextTick = new Date(store.getState().energyNextTick).getTime() + energyNextTickIncrement;
          store.setState({ energyNextTick });
          break;
        }
        case 'energyIncrement': {
          const energyIncrement = value as number;
          store.setState({ energyIncrement });
          break;
        }
        case 'energyMinuteInterval': {
          const energyMinuteInterval = value as number;
          store.setState({ energyMinuteInterval });
          break;
        }
        case 'spiritNextTick': {
          const spiritNextTickIncrement = value as number;
          const spiritNextTick = new Date(store.getState().spiritNextTick).getTime() + spiritNextTickIncrement;
          store.setState({ spiritNextTick });
          break;
        }
        case 'spiritMinuteInterval': {
          const spiritMinuteInterval = value as number;
          store.setState({ spiritMinuteInterval });
          break;
        }
        // TITLE: INVENTORY
        case 'inventory': {
          const inventory = value as ClientInventoryItem[];
          store.setState({ inventory });
          updateSortedInventory();

          break;
        }

        case 'equipment': {
          const equipment = value as ClientEquipmentItem[];
          store.setState({ equipment });
          updateMappedEquipment();

          break;
        }
        case 'updateInventoryItem': {
          const updateItem = value as ClientInventoryItem;
          store.setState((state) => ({
            ...state,
            inventory: state.inventory.map((item) => (item.id === updateItem.id ? updateItem : item)),
          }));
          updateSortedInventory();

          break;
        }
        case 'addInventoryItem': {
          const addItem = value as ClientInventoryItem;
          store.setState((state) => ({
            ...state,
            inventory: [...state.inventory, addItem],
          }));
          updateSortedInventory();

          break;
        }
        case 'removeInventoryItem': {
          const removeItem = value as ClientInventoryItem;
          store.setState((state) => ({
            ...state,
            inventory: state.inventory.filter((item) => item.id !== removeItem.id),
          }));
          updateSortedInventory();

          break;
        }
        case 'addEquipmentItem': {
          const addEquipment = value as ClientEquipmentItem;
          store.setState((state) => ({
            ...state,
            equipment: [...state.equipment, addEquipment],
          }));
          updateMappedEquipment();

          break;
        }
        case 'removeEquipmentItem': {
          const removeEquipment = value as ClientEquipmentItem;
          store.setState((state) => ({
            ...state,
            equipment: state.equipment.filter((item) => item.id !== removeEquipment.id),
          }));
          updateMappedEquipment();

          break;
        }
        // TITLE: COMBAT
        case 'combat': {
          combatStore.getState().resetCombat();
          // NOTE: This is received when you log in and are already in combat
          const combat = value as ClientCombat;
          // Reset combat reward
          combatStore.setState({ combatReward: defaultCombatReward });
          // NOTE: set combatants first so there is no race condition when selecting first combat target in Combatants.tsx
          combatStore.getState().setCombatants(combat.combatants);
          combatStore.getState().setCombatCurrentRound(combat.currentRound);
          combatStore.getState().setCombatLocationName(combat.locationName);

          if (store.getState().waitingForCombatStart) {
            combatStore.setState({ combatScreenState: CombatScreenState.InCombat });
            store.setState({ waitingForCombatStart: false });

            navigate(COMBAT, { replace: false });
          }

          break;
        }
        case 'combatants': {
          const combatants = value as ClientCombatantStatic[];
          combatStore.getState().setCombatants(combatants);
          break;
        }
        case 'combatStatus': {
          // Received combatStatus from the server so reset nextSequenceKey
          const combatStatus = value as CombatStatus;
          if (combatStatus > CombatStatus.Unknown) {
            combatStore.setState({ nextSequenceKey: '' });
          }

          combatStore.setState({ combatStatus });

          break;
        }
        case 'combatReward': {
          const combatReward = value as ClientCombatReward;
          combatStore.setState({ combatReward });

          break;
        }
        case 'combatLatestTurn': {
          // NOTE: Received a new turn from the server so add it to the queue
          const combatTurn = value as ClientCombatTurnUpdate;

          combatStore.getState().addTurnToCombatTurnQueue({
            turnNumber: combatTurn.turnNumber,
            roundNumber: combatTurn.roundNumber,
            turn: combatTurn.turn,
          });

          if (combatStore.getState().turnQueue.length > 0) {
            if (combatStore.getState().nextSequenceKey) {
              const foundTurnInQueue = combatStore
                .getState()
                .turnQueue.find((turn) => turn.turn.sequenceKey === combatStore.getState().nextSequenceKey);
              if (foundTurnInQueue) {
                addCombatTurn(foundTurnInQueue);
              }
            } else {
              // If there is no nextSequenceKey, add the first turn in the queue
              const firstTurnInQueue = combatStore.getState().turnQueue[0];

              if (firstTurnInQueue) {
                addCombatTurn(firstTurnInQueue);
              }
            }
          }

          combatStore.getState().appendTurnToCurrentRound(combatTurn);
          combatStore.getState().setCombatRoundCombatantsStats([
            {
              roundNumber: combatTurn.roundNumber,
              combatantsStats: combatStore.getState().currentRound.combatantsStats,
            },
          ]);

          for (const action of combatTurn.turn.actions) {
            const combatantStatic = combatStore.getState().combatants.find((combatant) => {
              return combatant.id === action.target.id && combatant.type === action.target.type;
            });
            if (action.killingBlow) {
              // Play error sound when user is defeated, normal killing blow otherwise
              if (action.target.type === 'user') {
                playActionSound('PLAYER_DEFEATED');
              } else {
                playKillingBlowSfx(combatantStatic?.killingBlowSfx);
              }
            } else {
              if (combatantStatic?.hitSfx && action.actor.type !== 'npc') {
                playHitSfx(combatantStatic.hitSfx, action.target.type === 'npc');
              }

              playSkillSfx(action.usageSfx, action.actor.type === 'npc');
            }
          }

          break;
        }
        case 'combatTurnExpireAt': {
          const turnExpireAt = value as number;

          combatStore.getState().setTurnExpireAt(turnExpireAt);

          break;
        }
        case 'currentRound': {
          // NOTE: Used when the round updates but the turn hasn't changed, for example when spawning an NPC
          const currentRound = value as ClientCombatRound;
          const combatRoundCombatantsStat: CombatRoundCombatantsStats = {
            roundNumber: currentRound.roundNumber,
            combatantsStats: currentRound.combatantsStats,
          };
          combatStore.getState().setCombatCurrentRound(currentRound);
          combatStore.getState().setCombatRoundCombatantsStats([combatRoundCombatantsStat]);

          break;
        }
        case 'combatRounds': {
          // NOTE: This is received when you log in and are already in combat
          // Past rounds arrive here; the current round is already set via the 'combat' event.
          const pastRounds = value as ClientCombatRound[];
          const currentRound = combatStore.getState().currentRound;
          const allRounds = currentRound.roundNumber > 0 ? [...pastRounds, currentRound] : pastRounds;

          const turnsText = allRounds
            .map((round: ClientCombatRound): CombatTurnData[] => {
              return round.turns.toReversed().map((turn: ClientCombatTurn, turnNumber: number): CombatTurnData => {
                return { roundNumber: round.roundNumber, turn, turnNumber };
              });
            })
            .toReversed();
          combatStore.getState().setCombatLogHistory(turnsText.flat());
          const combatRoundCombatantsStats = allRounds.map((combatRound) => {
            return {
              roundNumber: combatRound.roundNumber,
              combatantsStats: combatRound.combatantsStats,
            };
          });
          combatStore.getState().setCombatRoundCombatantsStats(combatRoundCombatantsStats);
          break;
        }
        // TITLE: NOTIFICATIONS
        case 'notifications': {
          const sanitisedNotifications = (value as ClientNotificationRecord[]).map((record) => {
            return {
              key: record.key,
              message: record.message,
              date: record.date,
              localeDate: record.date.toLocaleString(),
              link: record.link,
            };
          });
          store.setState({ notifications: sanitisedNotifications });
          break;
        }
        case 'addNotification': {
          store.getState().addNotification(value as ClientNotificationRecord);
          break;
        }
        // TITLE: MAIL
        case 'inbox': {
          const inbox = value as ClientMail[];
          mailStore.setState({ inbox });
          break;
        }
        case 'outbox': {
          const outbox = value as ClientMail[];
          mailStore.setState({ outbox });
          break;
        }
        case 'addInboxMail': {
          const mail = value as ClientMail;
          mailStore.getState().addInboxMail(mail);
          playActionSound('RECEIVE_MAIL');
          break;
        }
        case 'addOutboxMail': {
          const mail = value as ClientMail;
          mailStore.getState().addOutboxMail(mail);
          break;
        }
        // TITLE: LOCATION
        case 'location': {
          store.setState({ location: value as ClientLocation });
          store.setState({ changingLocation: false });
          break;
        }
        case 'area': {
          const area = value as ClientArea;
          const locationsMenu = generateLocationsMenu(area);

          // Update background music if soundtracks have changed
          const currentArea = store.getState().area;
          const playerName = store.getState().name;
          const musicContext = musicStore.getState().context;

          // Play area music if:
          // 1) Area actually changed (different area name), OR
          // 2) Transitioning from naming context (player just chose their name)
          // Skip if player hasn't chosen a name yet (still on InputName screen)
          const areaChanged = currentArea.name !== area.name;
          const leavingNamingContext = musicContext === 'naming' && playerName !== UNNAMED;

          if ((areaChanged || leavingNamingContext) && playerName !== UNNAMED) {
            playAreaMusic(area.soundtracks);
          }

          store.setState({ area, locationsMenu });
          marketStore.getState().resetMarket();
          store.setState({ changingLocation: false });

          break;
        }
        case 'changingArea': {
          if (typeof value === 'boolean') {
            store.setState({ locationLastEngagedAt: defaultLocation, changingArea: value });
          }
          break;
        }
        // TITLE: PARTY
        case 'party': {
          if (value === null) {
            store.setState({ party: undefined });
            break;
          }

          const party = value as ClientParty;
          store.getState().setParty(party);

          break;
        }
        case 'partyUserStatus': {
          const partyUserStatus = value as ClientPartyUserStatus;
          store.setState((state) => {
            return state.party
              ? {
                  ...state,
                  party: {
                    id: state.party.id,
                    name: state.party.name,
                    leaderName: state.party.leaderName,
                    members: state.party.members.map((member) => {
                      if (member.id === partyUserStatus.userId) {
                        return {
                          ...member,
                          status: partyUserStatus.status,
                        };
                      }
                      return member;
                    }),
                  },
                }
              : state;
          });

          break;
        }
        case 'partyLeaderEnteredCombat': {
          const autoRedirectToCombatInParty = persistentStore.getState().autoRedirectToCombatInParty;
          if (autoRedirectToCombatInParty) {
            navigate(COMBAT, { replace: false });
          }
          break;
        }
        // TITLE: CORPORATION
        case 'corporation': {
          if (value === null) {
            corporationStore.getState().resetState();
            break;
          }
          corporationStore.getState().setCorporation(value as ClientCorporation);
          break;
        }
        case 'updateCorporationInventoryItem': {
          const updateItem = value as ClientInventoryItem;
          corporationStore.setState((state) => ({
            ...state,
            inventory: state.inventory.map((item) =>
              item.item.name === updateItem.item.name && item.quality === updateItem.quality ? updateItem : item,
            ),
          }));
          updateCorporationSortedInventory();

          break;
        }
        case 'addCorporationInventoryItem': {
          const addItem = value as ClientInventoryItem;
          corporationStore.setState((state) => ({
            ...state,
            inventory: [...state.inventory, addItem],
          }));
          updateCorporationSortedInventory();

          break;
        }
        case 'removeCorporationInventoryItem': {
          const removeItem = value as ClientInventoryItem;
          corporationStore.setState((state) => ({
            ...state,
            inventory: state.inventory.filter((item) => item.id !== removeItem.id),
          }));
          updateCorporationSortedInventory();

          break;
        }

        case 'updateCorporationPrivateDescription': {
          const update = value as ClientUpdateCorporationPrivateDescription;
          corporationStore.setState((state) => ({
            ...state,
            privateDescription: update.description,
            privateDescriptionUpdatedAt: update.updatedAt,
          }));
          break;
        }

        case 'updateCorporationPublicDescription': {
          const update = value as ClientUpdateCorporationPublicDescription;
          corporationStore.setState((state) => ({
            ...state,
            publicDescription: update.description,
            publicDescriptionUpdatedAt: update.updatedAt,
          }));
          break;
        }

        case 'corporationList': {
          if (Array.isArray(value)) {
            corporationStore.setState((state) => ({
              ...state,
              list: value,
            }));
          }
          break;
        }

        case 'corporationJoinRequests': {
          if (Array.isArray(value)) {
            const joinRequests = value as ClientUserCorporationJoinRequest[];
            corporationStore.getState().setJoinRequests(joinRequests);
          }
          break;
        }

        case 'corporationEventLogs': {
          if (Array.isArray(value)) {
            corporationStore.getState().addEventLogs(value as ClientCorporationEventLog[]);
          }
          break;
        }

        case 'corporationEventLog': {
          corporationStore.getState().addEventLog(value as ClientCorporationEventLog);
          break;
        }
        case 'corporationActiveMissions': {
          if (Array.isArray(value)) {
            corporationStore.getState().setActiveMissions(value as ClientCorporationMissionRecord[]);
          }
          break;
        }
        case 'corporationCompleteMissions': {
          if (Array.isArray(value)) {
            corporationStore.getState().setCompleteMissions(value as ClientCorporationMissionRecord[]);
          }
          break;
        }
        case 'corporationCompleteMissionsRecords': {
          if (Array.isArray(value)) {
            corporationStore.getState().setCompleteMissionsRecords(value as CompleteCorporationMissionRecord[]);
          }
          break;
        }
        case 'corporationMissionBoard': {
          if (Array.isArray(value)) {
            corporationStore.getState().setMissionBoard(value as CorporationMissionBoardItem[]);
          }
          break;
        }

        case 'updateCorporationInfluence': {
          const influence = value as number;
          corporationStore.setState((state) => ({
            ...state,
            influence,
          }));
          break;
        }

        // TITLE: SKILLS
        case 'updateEquippedSkillOrder': {
          infoNotification({ message: 'New skill order saved' });
          break;
        }
        // TITLE: MULTIPLE HANDLER
        case 'multiple': {
          if (Array.isArray(value)) {
            for (const v of value) {
              handleData(v);
            }
          }
          break;
        }
        case 'stats': {
          if (isObject(value)) {
            store.setState({ ...value });
          }
          break;
        }
        case 'workoutStats': {
          if (isObject(value)) {
            store.getState().updateWorkoutStat(value as WorkoutRecord);
          }
          break;
        }
        case 'deleteAccountSuccess': {
          navigate(LOGOUT, { replace: true });
          break;
        }
        // TITLE: MISSIONS
        case 'addMissionPin': {
          const { id } = value as ClientAddMissionPin;
          persistentStore.getState().addPinnedMissionRecordId(id);
          break;
        }
        default: {
          store.setState({ [key]: value });
          break;
        }

        // TITLE: MARKET
        case 'ownMarketEntries': {
          const ownMarketEntries = value as ClientMarketBase[];
          marketStore.setState({ ownMarketEntries });
          break;
        }

        case 'marketOrders': {
          const marketOrders = value as ClientMarketResults;
          marketStore.setState({ marketOrders });
          break;
        }

        case 'marketListings': {
          const marketListings = value as ClientMarketResults;
          marketStore.setState({ marketListings });
          break;
        }

        case 'marketItems': {
          const marketItems = value as PopulatedItem[];
          const sortedMarketItems = marketItems
            .map(
              (item): ClientInventoryItem => ({
                id: item.name,
                item,
                quality: 0, // Quality is not used in this context
                quantity: 1, // Default quantity
                modules: [],
                moduleSlots: 0,
              }),
            )
            .toSorted((a, b) => a.item.name.localeCompare(b.item.name));
          marketStore.setState({ marketItems: sortedMarketItems });
          break;
        }
      }
    }
  } else {
    Logger.error('Invalid data:', data);
  }
}

type SocketExtended = Socket<ServerToClientEvents>;

export function socketEventHandler(socket: SocketExtended): void {
  const { handleGameServerConnected, setError } = authStore.getState();

  socket.on('loginVerified', (response) => {
    handleData(response.data);

    handleGameServerConnected();
    // store.setState({ reconnectAttempt: 0 });
    authStore.getState().resetError();

    if (response.error) {
      setError(response.error);
    }
  });

  socket.on('message', (messageData: unknown) => {
    handleData(messageData);
    // store.setState({ reconnectAttempt: 0 });
    authStore.getState().resetError();
  });
}
